/**
 * Searching a list of commands. Pure, so the palette's behaviour is testable
 * without a browser and without a database.
 *
 * WHY THE PALETTE TOOK A QUERY AND A LIST
 * ---------------------------------------
 * The palette navigates. It has no business reading the database, so every
 * command is a route and a label, supplied by the caller. That is what lets the
 * whole thing work offline, before any import, and with the data layer swapped
 * out from under it.
 *
 * WHY RANKING IS EXPLICIT
 * -----------------------
 * A palette that returns results in menu order makes people read. Typing
 * "invoice" should put "Invoices" first, not make the user scan past Quotations
 * and Sales Orders to find it. The ranks below are the only ordering rules, and
 * each one has a test.
 */

export interface Command {
  readonly id: string;
  readonly label: string;
  /** Section heading in the result list, e.g. "Sales". */
  readonly group: string;
  readonly keywords?: readonly string[];
  /** Where the command goes. Absent for commands that only act. */
  readonly href?: string;
  /** Rendered as a dim hint, e.g. a shortcut. */
  readonly hint?: string;
}

const MAX_RESULTS = 40;

/** Lower-cased, trimmed, and with runs of whitespace collapsed. */
function normalise(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * A crude singular form, so "deliveries" can find "Delivery Notes".
 *
 * This exists because of a test that typed "deliveries" and found nothing — a
 * search box that cannot find a screen by the obvious word for it is not a
 * search box. It is deliberately a HEURISTIC, applied only as an extra
 * candidate: both the exact and the singular text are tried, so a label that is
 * genuinely plural still matches an exactly-typed plural.
 */
function singular(word: string): string {
  if (word.endsWith('ies') && word.length > 3) return `${word.slice(0, -3)}y`;
  if (/(ss|sh|ch|x|z)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/**
 * 0 = exact label, 1 = label starts with, 2 = word in label,
 * 3 = label contains, 4 = keyword, 5 = no match.
 */
function rank(command: Command, query: string): number {
  const label = normalise(command.label);
  // Both forms of the query, and both forms of the label, are compared. This is
  // what makes "deliveries" reach "Delivery Notes" without making "Sales" reach
  // "Sale" only.
  const queries = query === singular(query) ? [query] : [query, singular(query)];
  const labels = label === singular(label) ? [label] : [label, singular(label)];

  for (const q of queries) {
    for (const l of labels) {
      if (l === q) return 0;
    }
  }
  for (const q of queries) {
    for (const l of labels) {
      if (l.startsWith(q)) return 1;
    }
  }
  // A whole-word match beats a mid-word one: "note" should find "Delivery
  // Notes", and not rank below a label that merely contains the letters.
  for (const q of queries) {
    for (const l of labels) {
      if (l.split(' ').some((word) => word.startsWith(q))) return 2;
    }
  }
  for (const q of queries) {
    for (const l of labels) {
      if (l.includes(q)) return 3;
    }
  }
  for (const q of queries) {
    if ((command.keywords ?? []).some((k) => normalise(k).includes(q))) return 4;
  }
  return 5;
}

const NO_MATCH = 5;

/**
 * Commands matching `query`, best first.
 *
 * An EMPTY query returns every command, not none. Opening the palette and
 * seeing nothing is how a user decides the feature is broken.
 */
export function searchCommands(commands: readonly Command[], query: string): Command[] {
  const q = normalise(query);
  if (q === '') return commands.slice(0, MAX_RESULTS);

  return commands
    .map((command) => ({ command, rank: rank(command, q) }))
    .filter((r) => r.rank < NO_MATCH)
    // Stable within a rank, so two equally good matches keep menu order and the
    // list does not shuffle between keystrokes as the user types.
    .sort((a, b) => a.rank - b.rank)
    .slice(0, MAX_RESULTS)
    .map((r) => r.command);
}

/**
 * Pin an index inside a list, or 0 when the list is empty.
 *
 * A CLAMP, not a wrap: an out-of-range index becomes the nearest real one.
 * Wrapping is `nextSelection`'s job, and the two being different is why this is
 * not simply a call to it — a caller that asks to clamp is asking for a
 * predictable value, not for a surprise jump to the other end of the list.
 */
export function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

/**
 * The selection after a key press. Arrow keys move; Home/End jump; everything
 * else leaves it alone.
 *
 * Wraps at both ends on purpose: a palette that stops at the last item makes
 * the arrow key feel broken.
 */
export function nextSelection(
  current: number,
  key: 'ArrowDown' | 'ArrowUp' | 'Home' | 'End',
  length: number,
): number {
  if (length <= 0) return 0;
  switch (key) {
    case 'ArrowDown':
      return current + 1 >= length ? 0 : current + 1;
    case 'ArrowUp':
      return current <= 0 ? length - 1 : current - 1;
    case 'Home':
      return 0;
    case 'End':
      return length - 1;
    default:
      return clampIndex(current, length);
  }
}

/**
 * Is this keystroke the palette's own?
 *
 * Ctrl+K is the documented shortcut. The other two are the platform
 * conventions for a quick-open dialog, and a user who has used either of them
 * elsewhere will try them here. `metaKey` is accepted for macOS.
 */
export function isPaletteShortcut(event: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  if (event.shiftKey || event.altKey) return false;
  if (!(event.ctrlKey || event.metaKey)) return false;
  const key = event.key.toLowerCase();
  return key === 'k' || key === 'p';
}
